import { RequestStatus } from 'features/request/requestSlice';
import { useRequest } from 'features/request/useRequest';
import { useResetStoreData } from 'features/request/useResetStoreData';

import { useCallback, useEffect } from 'react';
import { useDispatch } from 'react-redux';

import type { IVariablesData } from '../WorkOrderUseCoefficient';
import {
    uerApplyRatioToWorkOrderObjects,
    uerApplyWorkOrderPriceRatiosToRootObject,
} from '../workOrderUseCoefficientQueries';

interface IApplyRatioToWorkOrder {
    checkedIds: Record<string, boolean>;
    variablesData?: IVariablesData;
    closeHandler: () => void;
    updaterApplyRatio?: () => void;
}

export const useApplyRatioToWorkOrder = (props: IApplyRatioToWorkOrder) => {
    const { checkedIds, variablesData, closeHandler, updaterApplyRatio } = props;

    const priceListRatioIds = Object.keys(checkedIds)
        .filter((key) => checkedIds[key])
        .map(Number);

    const requestId = variablesData?.isRootItem ? 'applyWorkOrderToRootObject' : 'applyWorkOrderToObject';

    const dispatch = useDispatch();

    const onSuccess = useCallback(() => {
        updaterApplyRatio?.();
        closeHandler();
    }, [closeHandler, updaterApplyRatio]);

    const [, statusRootObject, , toRootObjectRequest] = useRequest({
        requestId,
        query: uerApplyWorkOrderPriceRatiosToRootObject,
        withEffect: false,
        dependenciesUpdater: onSuccess,
    });

    const [, statusObject, , toObjectRequest] = useRequest({
        requestId,
        query: uerApplyRatioToWorkOrderObjects,
        withEffect: false,
        dependenciesUpdater: onSuccess,
    });

    const isLoadingApply = variablesData?.isRootItem
        ? statusRootObject === RequestStatus.FETCHING
        : statusObject === RequestStatus.FETCHING;

    const addRatioToWorkOrderHandler = useCallback(() => {
        if (variablesData?.isRootItem) {
            dispatch(
                toRootObjectRequest({
                    workOrderObjectId: variablesData?.workOrderObjectIds,
                    priceListRatioIds,
                }),
            );
        } else {
            dispatch(
                toObjectRequest({
                    workOrderObjectIds: variablesData?.workOrderObjectIds,
                    priceListRatioIds,
                }),
            );
        }
    }, [
        dispatch,
        priceListRatioIds,
        toObjectRequest,
        toRootObjectRequest,
        variablesData?.isRootItem,
        variablesData?.workOrderObjectIds,
    ]);

    const [resetAddUerFromWorkOrder] = useResetStoreData({ requestId });

    useEffect(
        () => () => {
            resetAddUerFromWorkOrder(undefined);
        },
        [resetAddUerFromWorkOrder],
    );

    return {
        addRatioToWorkOrderHandler,
        isLoadingApply,
    };
};
